import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import {
  mapEntities,
  mapEntityTypeConfig,
  mapDashboardStats,
  mapScoreActions,
  mapCountries,
  mapCategories,
  mapLanguages,
  getMapEntitiesByType,
  getMapEntitiesByCountry,
  getMapEntitiesByCategory,
  getMapEntitiesByLanguage,
  getLiveMapEntities,
  getTopScoreMapEntities,
  getTrendingMapEntities,
  getUniqueStates,
  getUniqueCities,
  getCountryFlag,
  formatMapCount,
  type MapEntity,
  type MapEntityType,
} from '@/mocks/rfwMap';

type PageState = 'loading' | 'error' | 'ready';
type ViewMode = 'grid' | 'list';

export default function MapaRfwPage() {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [entityTypeFilter, setEntityTypeFilter] = useState<MapEntityType | 'todos'>('todos');
  const [countryFilter, setCountryFilter] = useState<string>('todos');
  const [stateFilter, setStateFilter] = useState<string>('todos');
  const [cityFilter, setCityFilter] = useState<string>('todos');
  const [categoryFilter, setCategoryFilter] = useState<string>('todos');
  const [languageFilter, setLanguageFilter] = useState<string>('todos');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedEntity, setSelectedEntity] = useState<MapEntity | null>(null);
  const [mapExpanded, setMapExpanded] = useState(false);

  const stats = mapDashboardStats;

  useEffect(() => {
    const t = setTimeout(() => setPageState('ready'), 500);
    return () => clearTimeout(t);
  }, []);

  const handleRetry = useCallback(() => {
    setPageState('loading');
    setTimeout(() => setPageState('ready'), 500);
  }, []);

  const filteredEntities = useMemo(() => {
    let result = mapEntities;
    if (entityTypeFilter !== 'todos') result = getMapEntitiesByType(entityTypeFilter);
    if (countryFilter !== 'todos') result = result.filter((e) => e.countryCode === countryFilter);
    if (stateFilter !== 'todos') result = result.filter((e) => e.state === stateFilter);
    if (cityFilter !== 'todos') result = result.filter((e) => e.city === cityFilter);
    if (categoryFilter !== 'todos') result = result.filter((e) => e.category === categoryFilter);
    if (languageFilter !== 'todos') result = result.filter((e) => e.language === languageFilter);
    return result;
  }, [entityTypeFilter, countryFilter, stateFilter, cityFilter, categoryFilter, languageFilter]);

  const uniqueStates = useMemo(() => getUniqueStates(), []);
  const uniqueCities = useMemo(() => getUniqueCities(), []);

  const hasActiveFilters = entityTypeFilter !== 'todos' || countryFilter !== 'todos' || stateFilter !== 'todos' || cityFilter !== 'todos' || categoryFilter !== 'todos' || languageFilter !== 'todos';

  const clearAllFilters = () => {
    setEntityTypeFilter('todos');
    setCountryFilter('todos');
    setStateFilter('todos');
    setCityFilter('todos');
    setCategoryFilter('todos');
    setLanguageFilter('todos');
  };

  const getEntityConfig = (type: MapEntityType) => mapEntityTypeConfig[type];

  /* ─── Loading State ─── */
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-background-50">
        <section className="relative w-full h-[340px] md:h-[480px] bg-background-200 animate-pulse" />
        <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 space-y-8">
          <div className="flex gap-2 flex-wrap">{[1,2,3,4,5,6].map((i) => (<div key={i} className="bg-background-100 rounded-full h-8 w-28 animate-pulse" />))}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">{[1,2,3,4,5,6,7,8].map((i) => (<div key={i} className="bg-background-100 rounded-2xl h-[340px] animate-pulse" />))}</div>
        </main>
      </div>
    );
  }

  /* ─── Error State ─── */
  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4 py-20">
        <div className="w-20 h-20 rounded-3xl bg-red-100 flex items-center justify-center mb-5">
          <i className="ri-error-warning-line text-3xl text-red-500"></i>
        </div>
        <h2 className="text-xl font-extrabold text-foreground-950 mb-2">Error al cargar el mapa</h2>
        <p className="text-sm text-foreground-500 mb-6 text-center max-w-md">No pudimos cargar los datos del mapa. Verificá tu conexión e intentá de nuevo.</p>
        <button onClick={handleRetry} className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-xl text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-refresh-line"></i>Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />

      <main className="flex-1 pt-16 md:pt-18">
        {/* ═══ HERO + MAP ═══ */}
        <section className={`relative w-full overflow-hidden transition-all duration-500 ${mapExpanded ? 'h-[520px] md:h-[640px]' : 'h-[340px] md:h-[480px]'}`}>
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d0!2d-72!3d6!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNsKwMDAnMDAuMCJOIDcywrAwMCcwMC4wIlc!5e0!3m2!1ses!2sar!4v1"
            className="absolute inset-0 w-full h-full"
            style={{ border: 0, filter: 'saturate(0.7) brightness(0.4)' }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Mapa Interactivo RFW"
          ></iframe>
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/15 to-black/65"></div>

          {/* Map Controls */}
          <button
            onClick={() => setMapExpanded(!mapExpanded)}
            className="absolute top-20 right-4 z-20 w-10 h-10 rounded-xl bg-background-50/90 backdrop-blur-md border border-background-50/20 flex items-center justify-center hover:bg-background-50 transition-all cursor-pointer"
          >
            <i className={`${mapExpanded ? 'ri-contract-up-down-line' : 'ri-expand-up-down-line'} text-foreground-700`}></i>
          </button>

          {/* Legend */}
          <div className="absolute top-20 left-4 z-20 flex flex-wrap gap-1.5">
            {(['radio','locutor','evento','club','oyente'] as MapEntityType[]).map((type) => {
              const config = mapEntityTypeConfig[type];
              return (
                <span key={type} className={`flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-semibold bg-background-50/90 backdrop-blur-md border border-background-200/30 ${config.textColor}`}>
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: config.markerColor }}></span>
                  {config.label}
                </span>
              );
            })}
          </div>

          {/* Hero Content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
            <div className="w-16 h-16 md:w-18 md:h-18 rounded-2xl bg-background-50/10 backdrop-blur-md flex items-center justify-center mb-4 border border-background-50/15">
              <i className="ri-earth-line text-3xl text-background-50"></i>
            </div>
            <h1 className="text-2xl md:text-4xl font-extrabold text-background-50 leading-tight mb-3">
              Mapa Interactivo RFW
            </h1>
            <p className="text-sm md:text-base text-background-50/80 max-w-xl leading-relaxed mb-5">
              Radios, locutores, eventos, clubes y oyentes — todo geolocalizado. Descubrí la comunidad RFW cerca tuyo.
            </p>
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <span className="px-3 py-1.5 rounded-full bg-background-50/10 backdrop-blur-md border border-background-50/20 text-background-50 text-[11px] font-semibold">
                <i className="ri-map-pin-line mr-1"></i>
                {stats.totalEntities} entidades
              </span>
              <span className="px-3 py-1.5 rounded-full bg-background-50/10 backdrop-blur-md border border-background-50/20 text-background-50 text-[11px] font-semibold">
                <i className="ri-global-line mr-1"></i>
                {stats.totalCountries} países
              </span>
              <span className="px-3 py-1.5 rounded-full bg-background-50/10 backdrop-blur-md border border-background-50/20 text-background-50 text-[11px] font-semibold">
                <i className="ri-pulse-line mr-1"></i>
                {stats.activeNow} activos ahora
              </span>
            </div>
          </div>
        </section>

        {/* ═══ MAIN CONTENT ═══ */}
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-12 space-y-8">
          {/* STATS STRIP */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { label: 'Entidades Mapeadas', value: stats.totalEntities, icon: 'ri-map-pin-line', color: 'text-primary-600 bg-primary-50' },
              { label: 'Países', value: stats.totalCountries, icon: 'ri-global-line', color: 'text-accent-600 bg-accent-50' },
              { label: 'Ciudades', value: stats.totalCities, icon: 'ri-building-line', color: 'text-emerald-600 bg-emerald-50' },
              { label: 'Activos Ahora', value: stats.activeNow, icon: 'ri-pulse-line', color: 'text-rose-600 bg-rose-50' },
              { label: 'Score Generado', value: formatMapCount(stats.totalScoreGenerated), icon: 'ri-flashlight-line', color: 'text-amber-600 bg-amber-50' },
            ].map((stat, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-100 border border-background-200/70">
                <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                  <i className={`${stat.icon} text-sm`}></i>
                </div>
                <div>
                  <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                  <p className="text-[10px] text-foreground-500">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* ═══ FILTERS ═══ */}
          <div className="space-y-4">
            {/* Type Filter */}
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-semibold text-foreground-500 mr-1 shrink-0">Tipo:</span>
              {([
                { id: 'todos' as const, label: 'Todos', icon: 'ri-grid-line', config: { bgColor: 'bg-background-100', textColor: 'text-foreground-700' } },
                ...(['radio','locutor','evento','club','oyente'] as MapEntityType[]).map((type) => ({
                  id: type,
                  label: mapEntityTypeConfig[type].label,
                  icon: mapEntityTypeConfig[type].icon,
                  config: mapEntityTypeConfig[type],
                })),
              ]).map((tab) => {
                const isActive = entityTypeFilter === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setEntityTypeFilter(tab.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap border cursor-pointer ${
                      isActive
                        ? `shadow-sm ${tab.id === 'todos' ? 'bg-background-100 text-foreground-700 border-background-300' : tab.config.bgColor + ' ' + tab.config.textColor + ' border-current'}`
                        : 'border-background-200 bg-background-50 text-foreground-500 hover:text-foreground-700 hover:border-background-300'
                    }`}
                  >
                    <i className={`${tab.icon} text-[10px]`}></i>
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Country Filter */}
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-semibold text-foreground-500 mr-1 shrink-0">País:</span>
              {[
                { id: 'todos' as string, label: 'Todos', flag: '🌎' },
                ...mapCountries.map((c) => ({ id: c.code, label: c.name, flag: c.flag })),
              ].map((tab) => {
                const isActive = countryFilter === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => { setCountryFilter(tab.id); setStateFilter('todos'); setCityFilter('todos'); }}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap border cursor-pointer ${
                      isActive
                        ? 'bg-accent-100 text-accent-700 border-accent-300 shadow-sm'
                        : 'border-background-200 bg-background-50 text-foreground-500 hover:text-foreground-700 hover:border-background-300'
                    }`}
                  >
                    <span className="text-sm">{tab.flag}</span>
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* State Filter */}
            {countryFilter !== 'todos' && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] font-semibold text-foreground-500 mr-1 shrink-0">Estado:</span>
                {[
                  { id: 'todos' as string, label: 'Todos' },
                  ...uniqueStates.filter((s) => mapEntities.some((e) => e.countryCode === countryFilter && e.state === s)).map((state) => ({ id: state, label: state })),
                ].map((tab) => {
                  const isActive = stateFilter === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setStateFilter(tab.id)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap border cursor-pointer ${
                        isActive
                          ? 'bg-emerald-100 text-emerald-700 border-emerald-300 shadow-sm'
                          : 'border-background-200 bg-background-50 text-foreground-500 hover:text-foreground-700 hover:border-background-300'
                      }`}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            )}

            {/* City Filter */}
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-semibold text-foreground-500 mr-1 shrink-0">Ciudad:</span>
              {[
                { id: 'todos' as string, label: 'Todas' },
                ...uniqueCities.map((city) => ({ id: city, label: city })),
              ].slice(0, 16).map((tab) => {
                const isActive = cityFilter === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setCityFilter(tab.id)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap border cursor-pointer ${
                      isActive
                        ? 'bg-violet-100 text-violet-700 border-violet-300 shadow-sm'
                        : 'border-background-200 bg-background-50 text-foreground-500 hover:text-foreground-700 hover:border-background-300'
                    }`}
                  >
                    <i className="ri-map-pin-line text-[10px]"></i>
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Category + Language Filters */}
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-semibold text-foreground-500 mr-1 shrink-0">Categoría:</span>
              {[
                { id: 'todos' as string, label: 'Todas', icon: 'ri-grid-line' },
                ...mapCategories.map((cat) => ({ id: cat, label: cat, icon: 'ri-price-tag-3-line' })),
              ].slice(0, 12).map((tab) => {
                const isActive = categoryFilter === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setCategoryFilter(tab.id)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap border cursor-pointer ${
                      isActive
                        ? 'bg-primary-100 text-primary-700 border-primary-300 shadow-sm'
                        : 'border-background-200 bg-background-50 text-foreground-500 hover:text-foreground-700 hover:border-background-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-semibold text-foreground-500 mr-1 shrink-0">Idioma:</span>
              {[
                { id: 'todos' as string, label: 'Todos' },
                ...mapLanguages.map((lang) => ({ id: lang, label: lang })),
              ].map((tab) => {
                const isActive = languageFilter === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setLanguageFilter(tab.id)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap border cursor-pointer ${
                      isActive
                        ? 'bg-amber-100 text-amber-700 border-amber-300 shadow-sm'
                        : 'border-background-200 bg-background-50 text-foreground-500 hover:text-foreground-700 hover:border-background-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Active Filters + Clear */}
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-foreground-400">
                {filteredEntities.length} de {mapEntities.length} entidades
              </span>
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="flex items-center gap-1 px-3 py-1 rounded-full bg-red-50 text-red-600 text-[10px] font-semibold hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-close-line text-[11px]"></i>
                  Limpiar filtros
                </button>
              )}
              <div className="flex items-center gap-1 ml-auto">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors cursor-pointer ${viewMode === 'grid' ? 'bg-primary-100 text-primary-600' : 'bg-background-100 text-foreground-400 hover:text-foreground-600'}`}
                >
                  <i className="ri-layout-grid-line text-sm"></i>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors cursor-pointer ${viewMode === 'list' ? 'bg-primary-100 text-primary-600' : 'bg-background-100 text-foreground-400 hover:text-foreground-600'}`}
                >
                  <i className="ri-list-check text-sm"></i>
                </button>
              </div>
            </div>
          </div>

          {/* ═══ ENTITY GRID / LIST ═══ */}
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {filteredEntities.map((entity) => {
                const config = getEntityConfig(entity.type);
                return (
                  <div
                    key={entity.id}
                    className="rounded-2xl bg-background-100 border border-background-200/70 overflow-hidden hover:border-primary-300/60 transition-all duration-300 group cursor-pointer"
                    onClick={() => setSelectedEntity(entity)}
                  >
                    <div className="relative">
                      <img src={entity.imageUrl} alt={entity.name} className="w-full h-36 object-cover object-top" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                      {entity.isLive && (
                        <span className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-500/90 text-white text-[9px] font-bold backdrop-blur-sm">
                          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                          EN VIVO
                        </span>
                      )}
                      <span className={`absolute bottom-3 left-3 px-2 py-0.5 rounded-full text-[9px] font-semibold ${config.bgColor} ${config.textColor} backdrop-blur-sm`}>
                        <i className={`${config.icon} text-[9px] mr-1`}></i>
                        {config.label}
                      </span>
                      <span className="absolute bottom-3 right-3 px-2 py-0.5 rounded-full bg-background-50/90 text-foreground-800 text-[9px] font-semibold backdrop-blur-sm">
                        {entity.score} pts
                      </span>
                    </div>
                    <div className="p-4">
                      <p className="text-sm font-bold text-foreground-900 leading-snug mb-0.5 line-clamp-1">{entity.name}</p>
                      <p className="text-[11px] text-foreground-500 mb-2 line-clamp-1">{entity.subtitle}</p>
                      <div className="flex items-center gap-2 text-[10px] text-foreground-400 mb-2">
                        <i className="ri-map-pin-line text-foreground-300"></i>
                        <span>{entity.city}, {entity.country}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-foreground-400 mb-2">
                        <i className="ri-price-tag-3-line text-foreground-300"></i>
                        <span>{entity.category}</span>
                        <span className="text-foreground-300">·</span>
                        <span>{entity.language}</span>
                      </div>
                      {entity.radioName && (
                        <div className="flex items-center gap-1 text-[10px] text-foreground-500 mb-2">
                          <i className="ri-radio-line text-foreground-300"></i>
                          <span>{entity.radioName}</span>
                          {entity.frequency && <span className="text-foreground-300">· {entity.frequency}</span>}
                        </div>
                      )}
                      <div className="flex items-center justify-between text-[10px] mb-2">
                        <span className="flex items-center gap-1 text-foreground-500">
                          <i className="ri-group-line"></i>
                          {formatMapCount(entity.totalFollowers)}
                        </span>
                        <span className={`flex items-center gap-1 ${entity.trend === 'up' ? 'text-emerald-600' : 'text-foreground-400'}`}>
                          <i className={`${entity.trend === 'up' ? 'ri-arrow-up-line' : 'ri-subtract-line'}`}></i>
                          +{entity.trendPercent}%
                        </span>
                      </div>
                      {entity.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 pt-2 border-t border-background-200/70">
                          {entity.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className="px-1.5 py-0.5 rounded-full bg-background-200/70 text-foreground-500 text-[8px] font-medium">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredEntities.map((entity) => {
                const config = getEntityConfig(entity.type);
                return (
                  <div
                    key={entity.id}
                    className="flex items-center gap-4 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200/60 transition-all duration-200 cursor-pointer"
                    onClick={() => setSelectedEntity(entity)}
                  >
                    <div className="w-14 h-14 rounded-xl overflow-hidden shrink-0">
                      <img src={entity.imageUrl} alt={entity.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm font-bold text-foreground-900">{entity.name}</p>
                        {entity.isLive && <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse shrink-0"></span>}
                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold ${config.bgColor} ${config.textColor}`}>
                          {config.label}
                        </span>
                      </div>
                      <p className="text-[11px] text-foreground-500">{entity.subtitle}</p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-foreground-400 mt-1">
                        <span className="flex items-center gap-1"><i className="ri-map-pin-line"></i>{entity.city}, {entity.country}</span>
                        <span className="flex items-center gap-1"><i className="ri-price-tag-3-line"></i>{entity.category}</span>
                        {entity.radioName && <span className="flex items-center gap-1"><i className="ri-radio-line"></i>{entity.radioName}</span>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-extrabold text-foreground-950">{entity.score}</p>
                      <p className="text-[9px] text-foreground-500">pts</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Empty State */}
          {filteredEntities.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
                <i className="ri-map-pin-line text-2xl text-foreground-300"></i>
              </div>
              <p className="text-sm font-semibold text-foreground-400">No se encontraron entidades</p>
              <p className="text-[12px] text-foreground-300 mt-1">Probá cambiando los filtros para descubrir más</p>
              <button onClick={clearAllFilters} className="mt-4 px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap">
                Limpiar todos los filtros
              </button>
            </div>
          )}

          {/* ═══ LIVE NOW STRIP ═══ */}
          {entityTypeFilter === 'todos' && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center">
                  <i className="ri-live-line text-base text-rose-600"></i>
                </div>
                <div>
                  <h3 className="text-sm font-extrabold text-foreground-950">En Vivo Ahora</h3>
                  <p className="text-xs text-foreground-500">{getLiveMapEntities().length} entidades transmitiendo</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {getLiveMapEntities().slice(0, 8).map((entity) => {
                  const config = getEntityConfig(entity.type);
                  return (
                    <Link
                      key={entity.id}
                      to={entity.linkUrl}
                      className="flex items-center gap-3 p-3 rounded-xl bg-background-100 border border-background-200/70 hover:border-rose-300/60 transition-all duration-200 group"
                    >
                      <div className="w-10 h-10 rounded-full overflow-hidden shrink-0 ring-2 ring-rose-200">
                        <img src={entity.imageUrl} alt={entity.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <p className="text-xs font-bold text-foreground-900 truncate">{entity.name}</p>
                          <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse shrink-0"></span>
                        </div>
                        <p className="text-[10px] text-foreground-500 truncate">{entity.city}, {entity.country}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold ${config.bgColor} ${config.textColor} shrink-0`}>
                        {config.label}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* ═══ SCORE SECTION ═══ */}
          <div className="rounded-3xl bg-background-100 border border-background-200/70 p-6 md:p-8">
            <div className="text-center mb-6">
              <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                <i className="ri-flashlight-line text-xl text-primary-600"></i>
              </div>
              <h3 className="text-lg font-extrabold text-foreground-950 mb-2">Score por Actividad Local</h3>
              <p className="text-xs text-foreground-500">Sumá puntos manteniendo tu presencia activa en el mapa RFW. Cada acción local cuenta.</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {mapScoreActions.map((action, i) => (
                <div key={i} className="p-4 rounded-xl bg-background-50 border border-background-200/70 text-center">
                  <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center mx-auto mb-2">
                    <i className={`${action.icon} text-base ${action.color}`}></i>
                  </div>
                  <p className="text-xs font-bold text-foreground-900 mb-1">{action.action}</p>
                  <p className="text-lg font-extrabold text-primary-600 mb-1">+{action.points} pts</p>
                  <p className="text-[10px] text-foreground-500 leading-snug">{action.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ═══ CROSS LINKS ═══ */}
          <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/radios-en-vivo" className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-rose-300/60 transition-all duration-200 text-center">
              <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-2">
                <i className="ri-radio-line text-base text-rose-600"></i>
              </div>
              <p className="text-sm font-semibold text-foreground-900">Radios en Vivo</p>
              <p className="text-[10px] text-foreground-500 mt-1">Descubrí qué están transmitiendo ahora</p>
            </Link>
            <Link to="/seguidores" className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200/60 transition-all duration-200 text-center">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-2">
                <i className="ri-user-heart-line text-base text-primary-600"></i>
              </div>
              <p className="text-sm font-semibold text-foreground-900">Seguidores RFW</p>
              <p className="text-[10px] text-foreground-500 mt-1">Seguí tus entidades favoritas</p>
            </Link>
            <Link to="/eventos" className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-emerald-300/60 transition-all duration-200 text-center">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-2">
                <i className="ri-calendar-event-line text-base text-emerald-600"></i>
              </div>
              <p className="text-sm font-semibold text-foreground-900">Eventos RFW</p>
              <p className="text-[10px] text-foreground-500 mt-1">Qué está pasando cerca tuyo</p>
            </Link>
          </section>

          {/* ═══ REGLA FINAL ═══ */}
          <section className="p-6 rounded-2xl bg-primary-100/70 border border-primary-200/50 text-center">
            <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
              <i className="ri-compass-3-line text-xl text-white"></i>
            </div>
            <p className="text-lg font-extrabold text-foreground-950 mb-2">El mapa debe ayudar a descubrir radios.</p>
            <p className="text-sm text-foreground-600 max-w-xl mx-auto">
              Cada pin en el mapa RFW representa una entidad real: una radio que quiere ser escuchada, un locutor que quiere conectar, un evento que quiere reunir, un club que quiere crecer, un oyente que quiere participar. El mapa no es decoración — es una herramienta viva de descubrimiento. Encontrá nuevas radios, conectate con comunidades y hacé crecer tu presencia radial sin fronteras geográficas.
            </p>
          </section>
        </div>
      </main>

      <Footer />

      {/* ═══ ENTITY DETAIL MODAL ═══ */}
      {selectedEntity && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedEntity(null)}></div>
          <div className="relative bg-background-50 rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
            <button
              onClick={() => setSelectedEntity(null)}
              className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-background-100 flex items-center justify-center hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line text-foreground-700"></i>
            </button>

            {/* Cover */}
            <div className="relative h-48 overflow-hidden rounded-t-3xl">
              <img src={selectedEntity.imageUrl} alt={selectedEntity.name} className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20"></div>
              <div className="absolute bottom-4 left-4 right-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold ${getEntityConfig(selectedEntity.type).bgColor} ${getEntityConfig(selectedEntity.type).textColor} backdrop-blur-sm`}>
                    {getEntityConfig(selectedEntity.type).label}
                  </span>
                  {selectedEntity.isLive && (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/90 text-white text-[9px] font-bold backdrop-blur-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                      EN VIVO
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-extrabold text-white">{selectedEntity.name}</h3>
                <p className="text-xs text-white/70">{selectedEntity.subtitle}</p>
              </div>
            </div>

            {/* Content */}
            <div className="p-5 space-y-5">
              {/* Location */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-xl bg-background-100">
                  <p className="text-[9px] font-semibold text-foreground-500 mb-0.5">País</p>
                  <p className="text-xs font-bold text-foreground-900 flex items-center gap-1">
                    <span>{getCountryFlag(selectedEntity.countryCode)}</span>
                    {selectedEntity.country}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-background-100">
                  <p className="text-[9px] font-semibold text-foreground-500 mb-0.5">Ciudad</p>
                  <p className="text-xs font-bold text-foreground-900 flex items-center gap-1">
                    <i className="ri-map-pin-line text-foreground-400"></i>
                    {selectedEntity.city}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-background-100">
                  <p className="text-[9px] font-semibold text-foreground-500 mb-0.5">Estado/Provincia</p>
                  <p className="text-xs font-bold text-foreground-900">{selectedEntity.state}</p>
                </div>
                <div className="p-3 rounded-xl bg-background-100">
                  <p className="text-[9px] font-semibold text-foreground-500 mb-0.5">Idioma</p>
                  <p className="text-xs font-bold text-foreground-900">{selectedEntity.language}</p>
                </div>
              </div>

              {/* Category + Radio */}
              <div className="p-3 rounded-xl bg-background-100">
                <p className="text-[9px] font-semibold text-foreground-500 mb-1">Categoría</p>
                <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-[10px] font-semibold">{selectedEntity.category}</span>
                {selectedEntity.radioName && (
                  <div className="mt-2 flex items-center gap-1 text-[11px] text-foreground-500">
                    <i className="ri-radio-line text-foreground-400"></i>
                    <span>{selectedEntity.radioName}</span>
                    {selectedEntity.frequency && <span className="text-foreground-300">· {selectedEntity.frequency}</span>}
                  </div>
                )}
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-2">
                <div className="p-3 rounded-xl bg-background-100 text-center">
                  <p className="text-lg font-extrabold text-foreground-950">{formatMapCount(selectedEntity.totalFollowers)}</p>
                  <p className="text-[9px] text-foreground-500">Seguidores</p>
                </div>
                <div className="p-3 rounded-xl bg-background-100 text-center">
                  <p className="text-lg font-extrabold text-primary-600">{selectedEntity.score}</p>
                  <p className="text-[9px] text-foreground-500">Score RFW</p>
                </div>
                <div className="p-3 rounded-xl bg-background-100 text-center">
                  <p className={`text-lg font-extrabold ${selectedEntity.trend === 'up' ? 'text-emerald-600' : 'text-foreground-400'}`}>
                    +{selectedEntity.trendPercent}%
                  </p>
                  <p className="text-[9px] text-foreground-500">Tendencia</p>
                </div>
              </div>

              {/* Tags */}
              {selectedEntity.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {selectedEntity.tags.map((tag) => (
                    <span key={tag} className="px-2 py-1 rounded-full bg-background-100 text-foreground-600 text-[10px] font-medium">{tag}</span>
                  ))}
                </div>
              )}

              {/* Action */}
              <Link
                to={selectedEntity.linkUrl}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-500 hover:bg-primary-600 text-white font-bold rounded-full text-sm transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-arrow-right-line"></i>
                Ir a {selectedEntity.name}
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}